import React, { useState, useEffect } from 'react';
import { CalendarDaysIcon, UsersIcon, BookOpenIcon } from '@heroicons/react/24/outline';
import { apiService } from '../services/api';

interface RciaSession {
  id: string;
  title: string;
  content: string;
  date: string;
  author: string;
  sessionNumber: number | null;
}

const Rcia: React.FC = () => {
  const [sessions, setSessions] = useState<RciaSession[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSessions();
  }, []);

  const loadSessions = async () => {
    setLoading(true);
    try {
      const data = await apiService.getRcia();
      setSessions(Array.isArray(data) ? data.filter(item => item !== null) as RciaSession[] : []);
    } catch (error) {
      console.error('Failed to load RCIA sessions:', error);
      setSessions([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-church-light to-white">
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-church-dark mb-4">
              RCIA
            </h1>
            <div className="w-24 h-1 bg-church-gold mx-auto mb-6"></div>
            <p className="text-xl text-gray-600">
              Rite of Christian Initiation of Adults
            </p>
          </div>

          {/* Content */}
          <div className="bg-white rounded-lg shadow-lg p-8">
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-church-blue mx-auto mb-4"></div>
                <p className="text-gray-600">Loading RCIA sessions...</p>
              </div>
            ) : sessions.length === 0 ? (
              <div className="text-center py-12">
                <BookOpenIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No Session Summaries Yet</h3>
                <p className="text-gray-600">
                  Check back soon for summaries from our RCIA sessions.
                </p>
              </div>
            ) : (
              <div className="space-y-8">
                <div className="mb-8">
                  <h2 className="text-2xl font-semibold text-church-dark mb-4">
                    Session Summaries
                  </h2>
                  <p className="text-gray-600">
                    Read summaries from our RCIA sessions below.
                  </p>
                </div>

                <div className="space-y-8">
                  {sessions.map((session) => (
                    <div key={session.id} className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow duration-200">
                      <div className="flex items-center mb-3">
                        <BookOpenIcon className="h-6 w-6 text-church-blue mr-3 flex-shrink-0" />
                        <h3 className="text-xl font-semibold text-church-dark">
                          {session.title}
                        </h3>
                      </div>

                      <div className="flex items-center text-gray-600 mb-4 space-x-6">
                        <div className="flex items-center">
                          <CalendarDaysIcon className="h-4 w-4 mr-2" />
                          <span className="text-sm">{formatDate(session.date)}</span>
                        </div>
                        <div className="flex items-center">
                          <UsersIcon className="h-4 w-4 mr-2" />
                          <span className="text-sm">{session.author}</span>
                        </div>
                      </div>

                      <div className="text-gray-700 leading-relaxed whitespace-pre-line">
                        {session.content}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-8 p-6 bg-church-light rounded-lg">
                  <h3 className="text-lg font-semibold text-church-dark mb-3">
                    About RCIA
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    The Rite of Christian Initiation of Adults (RCIA) is the process by which adults are welcomed into the Catholic Church.
                    Our sessions explore the Catholic faith through discussion, prayer, and community.
                    All are welcome to attend, whether you are considering joining the Church, want to learn more about the faith, or simply wish to deepen your understanding.
                  </p>
                  <p className="text-gray-700 leading-relaxed mt-3">
                    For more information about RCIA at All Saints, please contact the parish office at{' '}
                    <a href="mailto:allsaintsthirsk@rcdmidd.org.uk" className="text-church-blue hover:underline">
                      allsaintsthirsk@rcdmidd.org.uk
                    </a>{' '}
                    or call us at{' '}
                    <a href="tel:01845527011" className="text-church-blue hover:underline">
                      01845 527011
                    </a>.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Rcia;
